import React, { useState, useEffect } from "react"
import { Phone } from "lucide-react";
import { Mail } from "lucide-react";
import { MapPin } from "lucide-react";
import { axios_helper_async } from "helpers/Axios";
import { ContactUsViewModel } from "Common/ViewModels/ContactUsViewModel";
import withNotifications, { WithNotificationsProps } from "Common/withNotifications";
import { WithMessagesProps } from "Common/withMessages";
import { WithOverlayProps } from "Common/withOverlay";
import { MessageType } from "Common/Components/Messages/messageTypes";
import { WithTranslationProps } from "Common/Components/Translation/WithTranslationProps";
import withTranslation from "Common/withTranslation";
import withMessages from 'Common/withMessages';
import withOverlay from 'Common/withOverlay';
import { NotificationType } from "Common/Components/Notifications/notificationTypes";
import LoadingButton from "Common/Components/LoadingOverlay/LoadingButton/LoadingButton";


const ContactUs: React.FC<WithNotificationsProps & WithMessagesProps & WithTranslationProps> = ({
    messages,
    notifications,
    t,
    i18n
}) => {
    const modelFields: Record<keyof ContactUsViewModel, string> = {
        email: 'email',
        message: 'message',
        name: 'name',
        phone: 'phone',
        subject: 'subject',
        errors: 'errors'
    }

    const initialModel = {
        email: '',
        message: '',
        name: '',
        phone: '',
        subject: '',
        errors: {}
    }

    const [model, setModel] = useState<ContactUsViewModel>({
        email: '',
        message: '',
        name: '',
        phone: '',
        subject: '',
        errors: {
            email: '',
            message: '',
            name: '',
            phone: '',
            subject: ''
        }
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setModel(prevModel => ({
            ...prevModel,
            [name]: value
        }))
    }

    const validateModel = () => {
        let errors: ContactUsViewModel['errors'] = {};
        if (!model.email) errors.email = 'ContactUs.Validations.Email';
        if (!model.name) errors.name = 'ContactUs.Validations.Name';
        if (!model.subject) errors.subject = 'ContactUs.Validations.Subject';
        if (!model.message) errors.message = 'ContactUs.Validations.Message';

        setModel(prevModel => ({
            ...prevModel,
            errors
        }));

        if (Object.keys(errors).length !== 0) notifications.showNotification('General.CompleteData', NotificationType.Error);

        return Object.keys(errors).length === 0;
    }

    const handleClick = async () => {
        try {
            setIsLoading(true);
            if (validateModel()) {
                let response = await axios_helper_async<string>('/SOI/Send-Contact-Us-Form', 'POST', model);

                notifications.setNotifications(response.notifications);
                messages.setMessages(response.messages);

                if (!response.isSuccess) {
                    notifications.showNotification('AxiosAPI.InternalError', NotificationType.Error);
                }
                else {
                    notifications.showNotification('ContactUs.Alerts.EmailSent', NotificationType.Success);
                    setModel(initialModel);
                }
            }
        }
        catch (ex: any) {
            notifications.showNotification(`${ex}`, NotificationType.Error);
        }
        finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
    }, [model])

    return (
        <React.Fragment>
            <div className="relative py-14">
                <h1 className="mb-6 leading-normal text-center" data-aos="fade-left" data-aos-delay="300"><span className="relative inline-block px-2 mx-2 before:block before:absolute before:-inset-1 before:-skew-y-6 before:bg-sky-50 dark:before:bg-sky-500/20 before:rounded-md before:backdrop-blur-xl"><span className="relative text-custom-500">{t('ContactUs.ContactUs')}</span></span></h1>
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                    <div className="xl:col-span-3 bg-slate-800 card !mb-0" data-aos="fade-right" data-aos-delay="300">
                        <div className="felx card-body">
                            <div className="grid grid-cols-1 justify-center">
                                <div className="grid grid-cols-3 items-center py-3">
                                    <div className="flex justify-center items-center col-span-1">
                                        <MapPin className="text-white h-10 w-10" />
                                    </div>
                                    <div className="flex items-center col-span-2">
                                        <div>
                                            <h4 className="text-slate-50">{t('ContactUs.Location')}:</h4>
                                            <p className="text-slate-50">
                                                Cesar Augusto #315, Los Reyes, CP 78170, San Luis Potosí, S.L.P
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 items-center py-3">
                                    <div className="flex justify-center items-center col-span-1">
                                        <Mail className="text-white h-10 w-10" />
                                    </div>
                                    <div className="flex items-center col-span-2">
                                        <div>
                                            <h4 className="text-slate-50">{t('ContactUs.Email')}:</h4>
                                            <p className="text-slate-50"><a href="mailto:sales@soi-soluciones.com?subject=Questions">sales@soi-soluciones.com</a></p>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 items-end py-3">
                                    <div className="flex justify-center items-center col-span-1">
                                        <Phone className="text-white h-10 w-10" />
                                    </div>
                                    <div className="flex items-center col-span-2">
                                        <div>
                                            <h4 className="text-slate-50">{t('ContactUs.Phone')}:</h4>
                                            <p className="text-slate-50"><a href="tel:+524442654082">+52 444-265-4082</a></p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="xl:col-span-9" data-aos="fade-left" data-aos-delay="300">
                        <form id="MainForm">
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 mb-2">
                                <div>
                                    <label htmlFor={modelFields.name} className="inline-block mb-2 text-base font-medium text-">{t('ContactUs.Name')} <span className="text-red-500">*</span></label>
                                    <input type="text"
                                        id={modelFields.name}
                                        value={model.name}
                                        name={modelFields.name}
                                        onChange={handleChange}
                                        className={`form-input ${model.errors.name ? "border-red-500" : "border-slate-200"}`} placeholder={t('ContactUs.Name')} />
                                    {model.errors.name && <div className="mt-1 text-sm text-red-500">{t(model.errors.name)}</div>}
                                </div>
                                <div>
                                    <label htmlFor={modelFields.email} className="inline-block mb-2 text-base font-medium">{t('ContactUs.Email')} <span className="text-red-500">*</span></label>
                                    <input type="email"
                                        id={modelFields.email}
                                        value={model.email}
                                        name={modelFields.email}
                                        onChange={handleChange}
                                        className={`form-input ${model.errors.email ? "border-red-500" : "border-slate-200"}`} placeholder={t('ContactUs.Email')} />
                                    {model.errors.email && <div className="mt-1 text-sm text-red-500">{t(model.errors.email)}</div>}
                                </div>
                                <div>
                                    <label htmlFor={modelFields.phone} className="inline-block mb-2 text-base font-medium">{t('ContactUs.Phone')}</label>
                                    <input type="tel"
                                        id={modelFields.phone}
                                        name={modelFields.phone}
                                        value={model.phone}
                                        onChange={handleChange}
                                        className={`form-input ${model.errors.phone ? "border-red-500" : "border-slate-200"}`} placeholder={t('ContactUs.Phone')} />
                                    {model.errors.phone && <div className="mt-1 text-sm text-red-500">{t(model.errors.phone)}</div>}
                                </div>
                                <div>
                                    <label htmlFor={modelFields.subject} className="inline-block mb-2 text-base font-medium">{t('ContactUs.Subject')} <span className="text-red-500">*</span></label>
                                    <input type="text"
                                        id={modelFields.subject}
                                        name={modelFields.subject}
                                        value={model.subject}
                                        onChange={handleChange}
                                        className={`form-input ${model.errors.subject ? "border-red-500" : "border-slate-200"}`} placeholder={t('ContactUs.Subject')} />
                                    {model.errors.subject && <div className="mt-1 text-sm text-red-500">{t(model.errors.subject)}</div>}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-5">
                                <div>
                                    <label htmlFor={modelFields.message} className="inline-block mb-2 text-base font-medium">{t('ContactUs.Message')} <span className="text-red-500">*</span></label>
                                    <textarea
                                        id={modelFields.message}
                                        value={model.message}
                                        name={modelFields.message}
                                        onChange={handleChange}
                                        rows={5} className={`form-input ${model.errors.message ? "border-red-500" : "border-slate-200"}`} placeholder={t('ContactUs.Message')}></textarea>
                                    {model.errors.message && <div className="mt-1 text-sm text-red-500">{t(model.errors.message)}</div>}
                                </div>
                            </div>
                            <div className="mt-5 flex justify-center">
                                {isLoading ?
                                    <LoadingButton />
                                    :
                                    <button type="button" onClick={handleClick} className="text-white btn bg-custom-500 border-custom-500 w-100">{t('ContactUs.BtnSendMessage')}</button>
                                }
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
};

export default withMessages(withNotifications(withTranslation(ContactUs)));